/**
 * ComboboxField - Dropdown with support for custom values and expressions
 *
 * Based on LogicAppsUX Combobox pattern:
 * - Shows dropdown with options + "Enter custom value" option at bottom
 * - Users can type to filter options
 * - If typed value doesn't match, shows "Use 'X' as a custom value" option
 * - Selecting custom value switches to TokenizedInput with expression support
 * - Clear button returns to dropdown mode
 */

import { useState, useCallback, useRef, useMemo, useEffect, type ReactNode } from 'react';
import {
  makeStyles,
  mergeClasses,
  tokens,
  shorthands,
  Label,
  Text,
  Combobox,
  Option,
  Button,
  Tooltip,
  Spinner,
  type OptionOnSelectData,
  type SelectionEvents,
} from '@coreai/fluentui-react';
import { DismissRegular, ArrowClockwise16Regular } from '@fluentui/react-icons';
import { OpenRegular } from '@fluentui/react-icons';
import { TokenizedInput } from './TokenizedInput';
import { ExpressionPreview } from './ExpressionPreview';
import { useFieldPreviewFocus } from './useFieldPreviewFocus';
import { EndActionsSlot } from '../shared/EndActionsSlot';
import type { AgentParameterSchema } from './AgentParameterMenu';
import type { ValueSegment } from '@/types/valueSegment';
import {
  generateSegmentId,
  ValueSegmentType,
  isValueSegmentArray,
  segmentsToString,
} from '@/types/valueSegment';

import {
  fieldContainerStyle,
  fieldLabelRowStyle,
  fieldLabelStyle,
  fieldRequiredStyle,
} from './fieldStyles';

const useStyles = makeStyles({
  container: fieldContainerStyle,
  labelRow: fieldLabelRowStyle,
  label: fieldLabelStyle,
  required: fieldRequiredStyle,
  error: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteRedForeground1,
  },
  // Wrapper for input + preview overlay positioning
  inputWrapper: {
    position: 'relative',
  },
  // Custom mode editor container
  editorContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalXS,
  },
  editor: {
    flex: 1,
  },
  clearButton: {
    minWidth: '32px',
    height: '32px',
  },
  // Combobox styling
  combobox: {
    width: '100%',
  },
  // Custom option styling
  customOption: {
    color: tokens.colorBrandForeground1,
    fontStyle: 'italic',
  },
  // Divider in dropdown
  divider: {
    ...shorthands.margin(tokens.spacingVerticalXS, 0),
    ...shorthands.border('0'),
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  // Informational header rendered as a disabled Option (e.g. "No items").
  // Layout/padding come from the Option itself so it aligns with real options.
  // Fluent's disabled Option flips the text to the disabled color on hover and
  // keeps a pointer cursor; pin our muted color and use a default cursor so the
  // row doesn't look clickable.
  header: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    cursor: 'default',
    ':hover': {
      color: tokens.colorNeutralForeground3,
    },
  },
  // Combobox row with optional refresh button
  comboboxRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
  comboboxFlex: {
    flex: 1,
  },
  // Dependency waiting text
  waitingText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
  // Refresh button
  refreshButton: {
    minWidth: 'auto',
    padding: tokens.spacingHorizontalXS,
  },
  // Load error text
  loadError: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteRedForeground1,
  },
  externalLink: {
    alignSelf: 'flex-start',
  },
});

const Mode = {
  Default: 'Default',
  Custom: 'Custom',
} as const;
type Mode = (typeof Mode)[keyof typeof Mode];

// Sentinel option key for the multi-select "Enter custom value" entry. Picking
// it switches the whole field into shared Custom (expression) mode rather than
// adding a value to the selection, so `Mode` works the same as single-select.
const ENTER_CUSTOM_KEY = '__combobox_enter_custom__';

interface ComboboxOption {
  label: string;
  value: string;
  description?: string;
}

// Internal option type with additional metadata
interface InternalOption {
  key: string;
  value: string;
  displayName: string;
  disabled?: boolean;
  type?: 'normal' | 'custom' | 'customValue' | 'divider' | 'header';
}

export interface ComboboxFieldProps {
  name: string;
  label: string;
  value: string | string[] | ValueSegment[];
  options: ComboboxOption[];
  onChange: (value: string | string[] | ValueSegment[]) => void;
  description?: string | undefined;
  placeholder?: string | undefined;
  required?: boolean | undefined;
  disabled?: boolean | undefined;
  error?: string | undefined;
  /** Native browser autofill behavior for the underlying input. Defaults to off. */
  autoComplete?: 'off' | 'on' | undefined;
  /** Whether users can switch this field to freeform/expression mode. */
  allowCustomValue?: boolean | undefined;
  /** Show the agent parameter button - for tools within an agent */
  showAgentParameterButton?: boolean | undefined;
  /** The agent parameter schema from the tool definition */
  agentParameterSchema?: AgentParameterSchema | undefined;
  /** Action outputs from the last successful run, keyed by action name */
  actionOutputs?: Record<string, unknown> | undefined;
  /** Whether we have run data available for previews */
  hasRunData?: boolean | undefined;
  /** Whether the workflow has changed since the last run (data may be stale) */
  isStale?: boolean | undefined;
  // Dynamic options support
  /** Whether options are currently being loaded */
  isLoadingOptions?: boolean | undefined;
  /** Error message from loading options */
  loadError?: string | undefined;
  /** Callback to refresh/refetch options */
  onRefresh?: (() => void) | undefined;
  /** Message to show when waiting for dependencies */
  dependencyMessage?: string | undefined;
  /**
   * Message to show inside the dropdown when a dynamic list has finished
   * loading but returned zero options. Only set by dynamic fields once a fetch
   * has completed successfully (so it isn't shown while still loading).
   */
  noOptionsMessage?: string | undefined;
  /** Optional deep link displayed below the combobox. */
  externalLinkHref?: string | undefined;
  externalLinkLabel?: string | undefined;
  /** Field actions (e.g. FieldActionChips) rendered at the right of the label row. */
  endActions?: ReactNode | undefined;
  /**
   * Enable multi-select mode (from `x-ms-editor-options`). When true the field
   * lets the user pick several options, shows the selection as a comma-joined
   * value string in the input, and serializes the value as a `string[]` (or a
   * delimited scalar — see `multiSelectValueType`). Freeform custom values and
   * the shared Custom (expression) mode stay available, just like single-select.
   */
  multiSelect?: boolean | undefined;
  /**
   * How a multi-select value is serialized back through `onChange`
   * (`x-ms-editor-options.serialization.valueType`):
   * - `'array'` (default) → a `string[]`, e.g. `['a','b']`.
   * - `'string'` → a single `valueSeparator`-delimited scalar, e.g. `'a,b'`.
   * Reading tolerates both shapes regardless of this setting.
   */
  multiSelectValueType?: 'array' | 'string' | undefined;
  /**
   * Delimiter used when `multiSelectValueType === 'string'`, both to split an
   * incoming scalar and to join the outgoing one. Sourced from
   * `x-ms-editor-options.titleSeparator`; defaults to `,`.
   */
  valueSeparator?: string | undefined;
}

/**
 * Check if a combobox value contains an expression (${...}, @{...}, or
 * @function()). Expression values render in the custom (TokenizedInput) mode.
 */
export function isComboboxExpressionValue(text: string): boolean {
  return /\$\{[^}]+\}|@\{[^}]+\}|@[a-zA-Z_]+\(/.test(text);
}

function getSecureExternalLinkHref(href: string | undefined): string | undefined {
  if (!href) return undefined;
  try {
    const parsed = new URL(href);
    return parsed.protocol === 'https:' ? parsed.toString() : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Determine the initial mode based on value.
 */
function getInitialMode(
  stringValue: string,
  options: ComboboxOption[],
  multiSelect?: boolean
): Mode {
  if (!stringValue) {
    return Mode.Default;
  }
  // If value contains expressions, use custom mode
  if (isComboboxExpressionValue(stringValue)) {
    return Mode.Custom;
  }
  // Multi-select literals (arrays / JSON / single values) stay in Default.
  if (multiSelect) {
    return Mode.Default;
  }
  // If value matches an option, use default mode
  const matchesOption = options.some((opt) => opt.value === stringValue);
  if (matchesOption) {
    return Mode.Default;
  }
  // Value doesn't match any option - it's a custom value
  return Mode.Custom;
}

/**
 * ComboboxField - Dropdown with custom value and expression support
 */
export const ComboboxField = ({
  name,
  label,
  value,
  options,
  onChange,
  description,
  placeholder,
  required,
  disabled,
  error,
  autoComplete = 'off',
  allowCustomValue = true,
  showAgentParameterButton,
  agentParameterSchema,
  actionOutputs,
  hasRunData,
  isStale,
  // Dynamic options props
  isLoadingOptions,
  loadError,
  onRefresh,
  dependencyMessage,
  noOptionsMessage,
  externalLinkHref,
  externalLinkLabel,
  multiSelect,
  multiSelectValueType = 'array',
  valueSeparator = ',',
  endActions,
}: ComboboxFieldProps) => {
  const styles = useStyles();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const comboBoxRef = useRef<any>(null);
  const { isFocused, containerHandlers, previewHandlers, previewToggleSlotRef } = useFieldPreviewFocus();
  // null = user is not actively editing the input (show matched option label);
  // string (including '') = user has typed in the freeform combobox. This lets
  // the user fully clear the displayed text without it snapping back to the
  // selected option's label.
  const [searchValue, setSearchValue] = useState<string | null>(null);
  const [canAutoFocus, setCanAutoFocus] = useState(false);

  // Get string value from the input
  const stringValue = useMemo(() => {
    if (isValueSegmentArray(value)) {
      return segmentsToString(value);
    }
    return typeof value === 'string' ? value : '';
  }, [value]);

  // Track mode (dropdown vs custom editor)
  const [mode, setMode] = useState<Mode>(() =>
    allowCustomValue
      ? getInitialMode(stringValue, options, multiSelect)
      : Mode.Default
  );

  // Re-derive mode when the value changes externally. While editing in Custom
  // mode, the user returns to Default explicitly via the clear button — a
  // controlled value update must not collapse the editor on its own. Two cases
  // pin Custom mode: an empty value (single- or multi-select), and any value in
  // multi-select. The latter is required because multi-select's getInitialMode
  // always returns Default for literals, so picking "Use 'X'" / "Enter custom
  // value" (or typing a literal) would otherwise flip straight back to the
  // dropdown once the parent echoes the new value.
  useEffect(() => {
    setMode((current) =>
      current === Mode.Custom && (stringValue === '' || multiSelect)
        ? Mode.Custom
        : allowCustomValue
          ? getInitialMode(stringValue, options, multiSelect)
          : Mode.Default
    );
  }, [stringValue, options, multiSelect, allowCustomValue]);

  // Reset autoFocus flag after it's been used
  useEffect(() => {
    if (canAutoFocus && mode === Mode.Custom) {
      // Reset after a short delay to ensure focus has happened
      const timer = setTimeout(() => setCanAutoFocus(false), 100);
      return () => clearTimeout(timer);
    }
  }, [canAutoFocus, mode]);

  // Convert value to segments for TokenizedInput
  const normalizedValue: ValueSegment[] = useMemo(() => {
    if (isValueSegmentArray(value)) {
      return value;
    }
    if (typeof value === 'string' && value) {
      return [
        {
          id: generateSegmentId(),
          type: ValueSegmentType.LITERAL,
          value: value,
        },
      ];
    }
    return [];
  }, [value]);

  // Get the selected option key
  const selectedKey = useMemo(() => {
    const option = options.find((opt) => opt.value === stringValue);
    return option?.value ?? '';
  }, [options, stringValue]);

  // Build dropdown options with search filtering
  const comboboxOptions = useMemo((): InternalOption[] => {
    const result: InternalOption[] = [];
    const searchLower = (searchValue ?? '').toLowerCase();

    // Filter options based on search
    const filteredOptions = searchValue
      ? options.filter(
          (opt) =>
            opt.label.toLowerCase().includes(searchLower) ||
            opt.value.toLowerCase().includes(searchLower)
        )
      : options;

    // Add filtered options
    if (filteredOptions.length > 0) {
      for (const opt of filteredOptions) {
        result.push({
          key: opt.value,
          value: opt.value,
          displayName: opt.label,
          type: 'normal',
        });
      }
    } else if (searchValue) {
      // No matches found
      result.push({
        key: 'no-matches',
        value: '',
        displayName: 'No values match your search.',
        disabled: true,
        type: 'header',
      });
    } else if (noOptionsMessage) {
      // Dynamic list finished loading but returned zero items. Surface that
      // explicitly so the dropdown isn't just "Enter custom value".
      result.push({
        key: 'no-options',
        value: '',
        displayName: noOptionsMessage,
        disabled: true,
        type: 'header',
      });
    }

    // Add divider
    result.push({
      key: 'divider',
      value: '-',
      displayName: '-',
      type: 'divider',
    });

    // If user typed a value that doesn't match any option exactly, offer to use it
    if (
      allowCustomValue &&
      searchValue &&
      !options.some((opt) => opt.value === searchValue)
    ) {
      result.push({
        key: `use-${searchValue}`,
        value: searchValue,
        displayName: `Use "${searchValue}" as a custom value`,
        type: 'customValue',
      });
    }

    // Always show "Enter custom value" option
    if (allowCustomValue) {
      result.push({
        key: 'enter-custom',
        value: '',
        displayName: 'Enter custom value',
        type: 'custom',
      });
    }

    return result;
  }, [options, searchValue, noOptionsMessage, allowCustomValue]);

  // Handle option selection
  const handleOptionSelect = useCallback(
    (_: SelectionEvents, data: OptionOnSelectData) => {
      const optionValue = data.optionValue;
      if (!optionValue) return;

      const selectedOption = comboboxOptions.find((opt) => opt.key === optionValue);
      if (!selectedOption) return;

      if (selectedOption.type === 'custom') {
        // "Enter custom value" selected - switch to custom mode with empty value
        setMode(Mode.Custom);
        setCanAutoFocus(true);
        setSearchValue(null);
      } else if (selectedOption.type === 'customValue') {
        // "Use 'X' as a custom value" selected - switch to custom mode with the typed value
        onChange(selectedOption.value);
        setMode(Mode.Custom);
        setCanAutoFocus(true);
        setSearchValue(null);
      } else if (selectedOption.type === 'normal') {
        // Normal option selected
        onChange(selectedOption.value);
        setMode(Mode.Default);
        setSearchValue(null);
      }
    },
    [comboboxOptions, onChange]
  );

  // Handle search input. When the user empties the input, clear the underlying
  // value too — otherwise the displayed text would snap back to the selected
  // option's label on blur/close, making it impossible to clear the field.
  const handleInput = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      const inputValue = (e.target as HTMLInputElement).value;
      setSearchValue(inputValue);
      if (inputValue === '' && stringValue !== '') {
        onChange('');
      }
    },
    [onChange, stringValue]
  );

  // Handle custom input change
  const handleCustomChange = useCallback(
    (segments: ValueSegment[]) => {
      onChange(segments);
    },
    [onChange]
  );

  // Clear and return to dropdown mode
  const handleClear = useCallback(() => {
    onChange('');
    setMode(Mode.Default);
    setSearchValue(null);
    comboBoxRef.current?.focus();
  }, [onChange]);

  // Handle dropdown close — drop the transient search so the next open shows
  // the current value (or empty) rather than stale search text.
  const handleOpenChange = useCallback(
    (_: unknown, data: { open: boolean }) => {
      if (!data.open) {
        setSearchValue(null);
      }
    },
    []
  );

  // Get display value for combobox. When the user is actively typing
  // (searchValue !== null), honor that exactly — including the empty string —
  // so backspacing the last character doesn't snap back to the option label.
  const displayValue = useMemo(() => {
    if (searchValue !== null) {
      return searchValue;
    }
    const option = options.find((opt) => opt.value === stringValue);
    return option?.label ?? (allowCustomValue ? '' : stringValue);
  }, [searchValue, options, stringValue, allowCustomValue]);

  // ---------------------------------------------------------------------------
  // Multi-select mode (x-ms-editor-options.multiSelect)
  // ---------------------------------------------------------------------------

  // Normalize the incoming value to a string[] of selected option values.
  // Accepts an array (the canonical stored shape), a JSON-array string, a
  // `valueSeparator`-delimited scalar (when serialized as a string), or a
  // single non-empty string (wrapped as one selection).
  const selectedValues = useMemo<string[]>(() => {
    if (Array.isArray(value)) {
      return value.filter((v): v is string => typeof v === 'string');
    }
    if (typeof value === 'string' && value) {
      const trimmed = value.trim();
      if (trimmed.startsWith('[')) {
        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) {
            return parsed.map((v) => String(v));
          }
        } catch {
          // fall through to string-split / single-value wrapping
        }
      }
      // String serialization: split the delimited scalar into its members,
      // trimming whitespace and dropping empties (tolerates "a, b").
      if (multiSelectValueType === 'string') {
        return value
          .split(valueSeparator)
          .map((s) => s.trim())
          .filter((s) => s.length > 0);
      }
      return [value];
    }
    return [];
  }, [value, multiSelectValueType, valueSeparator]);

  // Comma-joined display text for the multi-select input (Fluent's "value
  // string" pattern). Falls back to the raw value when an option label isn't
  // known, e.g. stale/removed items.
  const selectedDisplayText = useMemo(() => {
    return selectedValues
      .map((val) => options.find((o) => o.value === val)?.label ?? val)
      .join(', ');
  }, [selectedValues, options]);

  // Fluent hands back the full new selection on each toggle. Normal picks are
  // persisted as-is; the two custom paths escape to whole-field Custom mode,
  // mirroring single-select where both "Use 'X'" and "Enter custom value" target
  // the entire field rather than a single set member.
  const handleMultiSelect = useCallback(
    (_: SelectionEvents, data: OptionOnSelectData) => {
      const picked = comboboxOptions.find(
        (opt) => (opt.type === 'custom' ? ENTER_CUSTOM_KEY : opt.value) === data.optionValue
      );
      if (picked?.type === 'custom' || picked?.type === 'customValue') {
        // "Enter custom value" starts empty; "Use 'X'" seeds the typed text.
        // Either way we leave the checklist and edit the whole field.
        if (picked.type === 'customValue') {
          onChange(picked.value);
        }
        setMode(Mode.Custom);
        setCanAutoFocus(true);
        setSearchValue(null);
        return;
      }
      // Normal toggle: persist Fluent's full new selection. Filter the sentinel
      // defensively so it can never land in the stored value. Serialize as a
      // delimited scalar or an array per `multiSelectValueType`.
      const nextValues = data.selectedOptions.filter((v) => v !== ENTER_CUSTOM_KEY);
      onChange(
        multiSelectValueType === 'string' ? nextValues.join(valueSeparator) : nextValues
      );
      // Keep the popup usable for further picks; clear the filter text.
      setSearchValue(null);
    },
    [comboboxOptions, onChange, multiSelectValueType, valueSeparator]
  );

  // Multi-select filter input. Unlike the single-select `handleInput`, emptying
  // the box only clears the filter — it never wipes the current selection.
  const handleMultiInput = useCallback((e: React.FormEvent<HTMLInputElement>) => {
    setSearchValue((e.target as HTMLInputElement).value);
  }, []);

  // Render option content
  const renderOption = (option: InternalOption) => {
    if (option.type === 'custom' || option.type === 'customValue') {
      return <span className={styles.customOption}>{option.displayName}</span>;
    }
    return <span>{option.displayName}</span>;
  };
  const secureExternalLinkHref = getSecureExternalLinkHref(externalLinkHref);

  return (
    <div className={styles.container} {...containerHandlers}>
      <div className={styles.labelRow}>
        <Label className={styles.label} htmlFor={name}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </Label>
        <EndActionsSlot previewToggleSlotRef={previewToggleSlotRef}>{endActions}</EndActionsSlot>
      </div>

      <div className={styles.inputWrapper}>
        {mode === Mode.Custom ? (
          // Custom mode - show TokenizedInput with clear button
          <div className={styles.editorContainer}>
            <div className={styles.editor}>
              <TokenizedInput
                name={name}
                value={normalizedValue}
                onChange={handleCustomChange}
                placeholder={placeholder || description || 'Enter value or expression...'}
                multiline={false}
                disabled={disabled}
                readonly={false}
                autoFocus={canAutoFocus}
                showAgentParameterButton={showAgentParameterButton}
                agentParameterSchema={agentParameterSchema}
                actionOutputs={actionOutputs}
              />
            </div>
            <Tooltip content="Clear custom value" relationship="label">
              <Button
                appearance="subtle"
                icon={<DismissRegular />}
                onClick={handleClear}
                disabled={disabled ?? false}
                className={styles.clearButton}
                aria-label="Clear custom value"
              />
            </Tooltip>
          </div>
        ) : dependencyMessage ? (
          // Waiting for dependencies
          <>
            <Combobox
              id={name}
              className={styles.combobox}
              disabled
              placeholder="Select required fields first..."
              aria-label={label}
              autoComplete={autoComplete}
            >
              <Option key="placeholder" value="" disabled>
                Waiting for dependencies...
              </Option>
            </Combobox>
            <Text className={styles.waitingText}>{dependencyMessage}</Text>
          </>
        ) : (
          // Default mode - show Combobox dropdown with optional refresh button.
          // The same control handles single- and multi-select: `multiselect` is
          // only true in our scenario, in which case selection is an array and
          // the current picks are shown as a comma-joined value string in the
          // input itself (Fluent's "value string" multiselect pattern). Custom
          // (expression) mode and the ExpressionPreview overlay are shared via
          // the branches above/below. While options are loading, the spinner is
          // rendered as the combobox's expand icon so the loading indicator sits
          // on the control itself.
          <div className={styles.comboboxRow}>
            <Combobox
              ref={comboBoxRef}
              id={name}
              className={mergeClasses(styles.combobox, styles.comboboxFlex)}
              multiselect={!!multiSelect}
              value={multiSelect ? (searchValue ?? selectedDisplayText) : displayValue}
              selectedOptions={
                multiSelect ? selectedValues : selectedKey ? [selectedKey] : []
              }
              onOptionSelect={multiSelect ? handleMultiSelect : handleOptionSelect}
              onInput={multiSelect ? handleMultiInput : handleInput}
              onOpenChange={handleOpenChange}
              placeholder={
                isLoadingOptions
                  ? 'Loading options...'
                  : placeholder ||
                    description ||
                    (multiSelect ? 'Select options...' : 'Select an option...')
              }
              disabled={disabled || isLoadingOptions}
              aria-invalid={!!error || !!loadError}
              aria-label={label}
              autoComplete={autoComplete}
              {...(isLoadingOptions ? { expandIcon: <Spinner size="tiny" /> } : {})}
              freeform={allowCustomValue}
              autoFocus={canAutoFocus}
            >
              {comboboxOptions.map((option) => {
                if (option.type === 'divider') {
                  return <hr key={option.key} className={styles.divider} />;
                }
                if (option.type === 'header') {
                  // Render informational headers (e.g. "No items") as a disabled
                  // Option so they share the exact check-icon slot + padding of
                  // real options like "Enter custom value" and align on the same
                  // vertical axis.
                  return (
                    <Option
                      key={option.key}
                      value={option.key}
                      text={option.displayName}
                      disabled
                      className={styles.header}
                    >
                      {option.displayName}
                    </Option>
                  );
                }
                // Single-select resolves the picked option by `key`. Multi-select
                // folds each normal `value` into `selectedOptions`. Both custom
                // paths escape to whole-field Custom mode instead: "Enter custom
                // value" via a sentinel, "Use 'X'" via its typed value (resolved
                // back to its `customValue` type in handleMultiSelect).
                //
                // In multi-select the custom paths are mode-switch actions, not
                // toggleable set members, so suppress the checkmark slot for them
                // (`checkIcon={null}`) — a checkbox would wrongly imply they add
                // to the selection. Normal options keep their checkmark.
                const isCustomAction =
                  option.type === 'custom' || option.type === 'customValue';
                return (
                  <Option
                    key={option.key}
                    value={
                      multiSelect
                        ? option.type === 'custom'
                          ? ENTER_CUSTOM_KEY
                          : option.value
                        : option.key
                    }
                    text={option.displayName}
                    disabled={option.disabled ?? false}
                    {...(multiSelect && isCustomAction ? { checkIcon: null } : {})}
                  >
                    {renderOption(option)}
                  </Option>
                );
              })}
            </Combobox>
            {onRefresh && !isLoadingOptions && (
              <Button
                className={styles.refreshButton}
                appearance="subtle"
                icon={<ArrowClockwise16Regular />}
                onClick={onRefresh}
                title="Refresh options"
                aria-label="Refresh options"
              />
            )}
          </div>
        )}

        {/* Show preview as overlay when focused and has value */}
        {isFocused && stringValue && (
          <ExpressionPreview
            value={stringValue}
            actionOutputs={actionOutputs}
            hasRunData={hasRunData}
            isStale={isStale}
            mode="overlay"
            {...previewHandlers}
          />
        )}
      </div>

      {secureExternalLinkHref && (
        <Button
          appearance="transparent"
          size="small"
          icon={<OpenRegular />}
          className={styles.externalLink}
          as="a"
          href={secureExternalLinkHref}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`${name}-external-link`}
        >
          {externalLinkLabel ?? 'Edit at source'}
        </Button>
      )}

      {error && (
        <Text className={styles.error} role="alert">
          {error}
        </Text>
      )}

      {loadError && !error && (
        <Text className={styles.loadError} role="alert">
          {loadError}
        </Text>
      )}
    </div>
  );
};

ComboboxField.displayName = 'ComboboxField';
